import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { BlogAnalytics, TopicPerformance, KeywordAnalytics } from '@/types/analytics';

export const useAnalytics = () => {
  const location = useLocation();
  const hasTrackedRef = useRef<string | null>(null);

  const trackPageView = useCallback(async () => {
    // Avoid duplicate tracking for the same path
    const currentPath = location.pathname + location.search;
    if (hasTrackedRef.current === currentPath) return;
    hasTrackedRef.current = currentPath;

    try {
      await supabase
        .from('analytics')
        .insert({
          page_path: currentPath,
          referrer: document.referrer || null,
          user_agent: navigator.userAgent,
          ip_address: null
        });
    } catch (error) {
      // Silently fail - analytics shouldn't break the app
      console.debug('Analytics tracking skipped');
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    // Defer analytics tracking to after page paint using requestIdleCallback
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(() => trackPageView(), { timeout: 2000 });
      return () => window.cancelIdleCallback(id);
    } else {
      // Fallback for Safari - use setTimeout with delay
      const timeoutId = setTimeout(trackPageView, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [trackPageView]);

  // Blog-specific analytics functions
  const trackBlogView = async (blogPostId: string, topicTemplateId?: string) => {
    try {
      // Track page view
      await trackPageView();
      
      // Track blog-specific metrics using analytics table
      const { data: existing } = await supabase
        .from('analytics')
        .select('*')
        .eq('page_path', `/blog/${blogPostId}`)
        .single();

      const isUniqueView = !existing || !localStorage.getItem(`viewed_${blogPostId}`);
      
      if (existing) {
        await supabase
          .from('analytics')
          .insert({
            page_path: `/blog/${blogPostId}`,
            user_agent: navigator.userAgent,
            referrer: document.referrer
          });
      } else {
        await supabase
          .from('analytics')
          .insert({
            page_path: `/blog/${blogPostId}`,
            user_agent: navigator.userAgent,
            referrer: document.referrer || null
          });
      }

      if (isUniqueView) {
        localStorage.setItem(`viewed_${blogPostId}`, 'true');
      }
    } catch (error) {
      console.debug('Blog analytics tracking failed:', error);
    }
  };

  const trackEngagement = async (blogPostId: string, metrics: {
    timeOnPage?: number;
    scrollDepth?: number;
    socialShares?: number;
  }) => {
    try {
      await supabase
        .from('analytics')
        .insert({
          page_path: `/blog/${blogPostId}`,
          user_agent: navigator.userAgent,
          referrer: document.referrer
        });
    } catch (error) {
      console.debug('Engagement tracking failed:', error);
    }
  };

  const getTrafficSource = (): string => {
    const referrer = document.referrer;
    if (!referrer) return 'direct';
    if (referrer.includes('google')) return 'organic';
    if (referrer.includes('facebook') || referrer.includes('twitter') || referrer.includes('linkedin')) return 'social';
    return 'referral';
  };

  const getDeviceType = (): string => {
    const width = window.innerWidth;
    if (width <= 768) return 'mobile';
    if (width <= 1024) return 'tablet';
    return 'desktop';
  };

  return {
    trackBlogView,
    trackEngagement
  };
};